import { Node, mergeAttributes, type Editor, type Range } from '@tiptap/react';

/**
 * Tiptap-нода «Выделенный блок» (callout / admonition) — 7 видов под кит.
 *
 *   info       — Примечание      синий     📝
 *   success    — Готово          зелёный   ✅
 *   danger     — Ошибка          красный   ⛔
 *   attention  — Внимание        серый     👁
 *   warning    — Предупреждение  жёлтый    ⚠️
 *   tip        — Совет           фиолетовый 💡
 *   idea       — Идея            рыжий     🔥
 *
 * Round-trip:
 *   editor HTML  →  <div class="fk-callout fk-callout-X" data-callout="X">…</div>
 *   turndown     →  GitHub-admonition blockquote `> [!X]` (X = uppercase code)
 *   markdown-it  →  обратно в тот же div (frontend и backend).
 *
 * Эмодзи/цвет каждого вида — фиксированы CSS'ом по data-callout (см. global.css),
 * поэтому всё чисто round-trip'ится через 7 именованных маркеров без потери стиля.
 */

export interface CalloutType {
  code: CalloutSeverity;
  label: string;
  emoji: string;
  color: string;
}

/** Канонический список 7 видов (используется slash-меню + bubble-тулбаром). */
export const CALLOUT_TYPES: CalloutType[] = [
  { code: 'info', label: 'Примечание', emoji: '📝', color: '#0096FF' },
  { code: 'success', label: 'Готово', emoji: '✅', color: '#2E9E5B' },
  { code: 'danger', label: 'Ошибка', emoji: '⛔', color: '#E53935' },
  { code: 'attention', label: 'Внимание', emoji: '👁', color: '#6B7280' },
  { code: 'warning', label: 'Предупреждение', emoji: '⚠️', color: '#E5A100' },
  { code: 'tip', label: 'Совет', emoji: '💡', color: '#7E3FB8' },
  { code: 'idea', label: 'Идея', emoji: '🔥', color: '#FF6900' },
];

export type CalloutSeverity =
  | 'info'
  | 'success'
  | 'danger'
  | 'attention'
  | 'warning'
  | 'tip'
  | 'idea';

const SEVERITIES: CalloutSeverity[] = [
  'info',
  'success',
  'danger',
  'attention',
  'warning',
  'tip',
  'idea',
];

export function normalizeSeverity(value: unknown): CalloutSeverity {
  const v = String(value ?? '').toLowerCase();
  return (SEVERITIES as string[]).includes(v) ? (v as CalloutSeverity) : 'info';
}

export const Callout = Node.create({
  name: 'callout',
  group: 'block',
  content: 'block+',
  defining: true,

  addAttributes() {
    return {
      severity: {
        default: 'info' as CalloutSeverity,
        parseHTML: (element) =>
          normalizeSeverity(
            element.getAttribute('data-callout') ?? element.getAttribute('data-severity'),
          ),
        renderHTML: (attributes) => ({
          'data-callout': normalizeSeverity(attributes.severity),
        }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-callout]' }];
  },

  renderHTML({ HTMLAttributes }) {
    const severity = normalizeSeverity(HTMLAttributes['data-callout']);
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        class: `fk-callout fk-callout-${severity}`,
        'data-callout': severity,
      }),
      0,
    ];
  },
});

/**
 * Вставить блок «Выделенный блок» заданного вида в позицию range
 * (slash-команда). Оборачивает текущий параграф в callout.
 */
export function insertCallout(
  editor: Editor,
  range: Range,
  severity: CalloutSeverity,
): void {
  editor
    .chain()
    .focus()
    .deleteRange(range)
    .wrapIn('callout', { severity: normalizeSeverity(severity) })
    .run();
}
